import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ApiService } from '../../../core/services/api.service';
import { PageTitleService } from '../../../core/services/page-title.service';
import { AppDatePipe } from '../../../core/pipes/app-date.pipe';

@Component({
  selector: 'app-book-exchange',
  standalone: true,
  imports: [CommonModule, FormsModule, AppDatePipe],
  templateUrl: './book-exchange.component.html',
  styleUrl: './book-exchange.component.scss',
})
export class BookExchangeComponent implements OnInit {
  /** When provided, skip the search step and show transactions immediately. */
  @Input() preselectedMember: any = null;
  /** When true, renders as a fixed overlay; false = renders as a page card. */
  @Input() asModal = false;
  @Output() closed = new EventEmitter<void>();

  // ── Search ─────────────────────────────────────────────────────────────────
  searchNo   = '';
  searchMemNo = '';
  searchName = '';
  searchPhone = '';

  private allMembers: any[] = [];
  memberSuggestions: any[] = [];
  isLoadingMembers = false;
  hasSearched = false;
  searchError = '';

  // ── Selected member & transactions ────────────────────────────────────────
  selectedMember: any = null;
  transactions: any[] = [];
  isLoadingTxns = false;
  txnError = '';
  selectedTxn: any = null;

  view: 'search' | 'transactions' = 'search';

  constructor(private api: ApiService, private pageTitle: PageTitleService) {}

  ngOnInit(): void {
    if (!this.asModal) {
      this.pageTitle.set('Reports', 'Books issued to and received from a member');
    }
    if (this.preselectedMember) {
      this.selectedMember = this.preselectedMember;
      this.view = 'transactions';
      this.loadTransactions(this.preselectedMember._id || this.preselectedMember.id);
    }
  }

  // ── Member search ──────────────────────────────────────────────────────────

  private loadAllMembers(): Promise<void> {
    if (this.allMembers.length) return Promise.resolve();
    this.isLoadingMembers = true;
    return new Promise((resolve) => {
      this.api.getMembers().subscribe({
        next: (members) => {
          this.allMembers = members || [];
          this.isLoadingMembers = false;
          resolve();
        },
        error: () => {
          this.isLoadingMembers = false;
          resolve();
        },
      });
    });
  }

  // ── Fuzzy helpers ─────────────────────────────────────────────────────────

  /** Collapse Indian-name long-vowel transliterations: aa→a, ee→i, oo→u */
  private normalizePhonetic(s: string): string {
    return s.toLowerCase()
      .replace(/aa/g, 'a')
      .replace(/ee/g, 'i')
      .replace(/oo/g, 'u')
      .replace(/(.)\1/g, '$1');   // collapse double consonants
  }

  /** Space-optimised Levenshtein distance */
  private levenshtein(a: string, b: string): number {
    const m = a.length, n = b.length;
    const row = Array.from({ length: n + 1 }, (_, i) => i);
    for (let i = 1; i <= m; i++) {
      let prev = row[0]++;
      for (let j = 1; j <= n; j++) {
        const tmp = row[j];
        row[j] = a[i - 1] === b[j - 1] ? prev : 1 + Math.min(prev, row[j], row[j - 1]);
        prev = tmp;
      }
    }
    return row[n];
  }

  /**
   * Returns a match score for one token against one field word (lower = better).
   * Infinity = no match.
   * 0 = exact prefix · 1 = exact substring · 2 = phonetic substring
   * 3+ = Levenshtein distance (capped at maxEdits)
   */
  private tokenScore(fieldWord: string, token: string): number {
    const fw  = fieldWord.toLowerCase();
    const fwN = this.normalizePhonetic(fw);
    const tN  = this.normalizePhonetic(token);
    if (fw.startsWith(token))  return 0;
    if (fw.includes(token))    return 1;
    if (fwN.includes(tN))      return 2;
    const maxEdits = token.length <= 5 ? 1 : 2;
    const dist = this.levenshtein(fwN, tN);
    return dist <= maxEdits ? 3 + dist : Infinity;
  }

  /**
   * Returns true when every whitespace token in `query` fuzzy-matches at least
   * one word in `field`. Also returns the cumulative score (used for sorting).
   */
  private fuzzyMatch(field: string, query: string): { match: boolean; score: number } {
    const tokens   = query.toLowerCase().trim().split(/\s+/).filter(Boolean);
    const words    = field.toLowerCase().split(/\s+/).filter(Boolean);
    let totalScore = 0;
    for (const token of tokens) {
      const best = Math.min(...words.map(w => this.tokenScore(w, token)));
      if (!isFinite(best)) return { match: false, score: Infinity };
      totalScore += best;
    }
    return { match: true, score: totalScore };
  }

  async doSearch(): Promise<void> {
    this.searchError = '';
    this.memberSuggestions = [];
    this.hasSearched = false;

    const no    = this.searchNo.trim();
    const memNo = this.normalizeMemNo(this.searchMemNo);
    const name  = this.searchName.trim();
    const phone = this.searchPhone.trim();

    if (!no && !memNo && !phone && name.length < 3) {
      this.searchError = 'Enter a member no, membership no, phone, or at least 3 characters of the name.';
      return;
    }

    await this.loadAllMembers();
    this.hasSearched = true;

    type Scored = { member: any; score: number };
    const scored: Scored[] = [];

    for (const m of this.allMembers) {
      if (no    && String(m.memberSrNo || m.memNo || '') !== no)  continue;
      if (phone && !(m.phone || '').includes(phone))              continue;
      if (memNo) {
        const score = this.memNoScore(m.memNo, memNo);
        if (!isFinite(score)) continue;
        scored.push({ member: m, score });
        continue;
      }
      if (name) {
        const { match, score } = this.fuzzyMatch(m.name || '', name);
        if (!match) continue;
        scored.push({ member: m, score });
      } else {
        scored.push({ member: m, score: 0 });
      }
    }

    // Sort by match quality (lower score = better), then alphabetically
    scored.sort((a, b) => a.score - b.score || (a.member.name || '').localeCompare(b.member.name || ''));

    this.memberSuggestions = scored.slice(0, 25).map(s => s.member);
    if (!this.memberSuggestions.length) {
      this.searchError = 'No members found matching your search.';
    }
  }

  /** Upper-cased, whitespace-stripped memNo for comparison ("hd / 12" → "HD/12"). */
  private normalizeMemNo(v: any): string {
    return String(v ?? '').replace(/\s+/g, '').toUpperCase();
  }

  /**
   * Membership-no match score (lower = better, Infinity = no match).
   * 0 = exact memNo ("HD/12") · 1 = serial part only, when the query has no
   * prefix ("12" finds "HD/12", "C/12" and plain "12").
   */
  private memNoScore(memberMemNo: any, query: string): number {
    const mn = this.normalizeMemNo(memberMemNo);
    if (!mn) return Infinity;
    if (mn === query) return 0;
    if (!query.includes('/') && mn.split('/').pop() === query) return 1;
    return Infinity;
  }

  searchByField(field: 'no' | 'memNo' | 'name' | 'phone'): void {
    if (field !== 'no')    this.searchNo    = '';
    if (field !== 'memNo') this.searchMemNo = '';
    if (field !== 'name')  this.searchName  = '';
    if (field !== 'phone') this.searchPhone = '';
    this.doSearch();
  }

  selectMember(member: any): void {
    this.selectedMember = member;
    this.view = 'transactions';
    this.loadTransactions(member._id || member.id);
  }

  changeSearch(): void {
    this.view = 'search';
    this.selectedMember = null;
    this.transactions = [];
    this.selectedTxn = null;
    this.txnError = '';
    // memberSuggestions, hasSearched and search inputs kept intentionally
    // so the previous list is still visible on return.
  }

  // ── Transactions ───────────────────────────────────────────────────────────

  loadTransactions(memberId: string): void {
    this.isLoadingTxns = true;
    this.txnError = '';
    this.transactions = [];
    this.selectedTxn = null;
    this.api.getTransactionsByMember(memberId).subscribe({
      next: (txns) => {
        this.transactions = (txns || [])
          .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
        this.isLoadingTxns = false;
      },
      error: () => {
        this.txnError = 'Failed to load transactions.';
        this.isLoadingTxns = false;
      },
    });
  }

  openTxnDetail(txn: any): void { this.selectedTxn = txn; }
  closeTxnDetail(): void        { this.selectedTxn = null; }

  memberStatusBadge(m: any): { label: string; cls: string } {
    const st = (m?.status || '').toUpperCase();
    if (st === 'CLOSED') return { label: 'Closed',   cls: 'badge bg-danger-subtle text-danger' };
    if (st === 'ONHOLD') return { label: 'On Hold',  cls: 'badge bg-warning-subtle text-warning' };
    const exp = m?.membershipExpiresOn ? new Date(m.membershipExpiresOn) : null;
    if (!exp) return { label: 'Active', cls: 'badge bg-success-subtle text-success' };
    const days = Math.floor((exp.getTime() - Date.now()) / 86400000);
    if (days < 0)   return { label: 'Closed',   cls: 'badge bg-danger-subtle text-danger' };
    if (days <= 30) return { label: 'Expiring', cls: 'badge bg-warning-subtle text-warning' };
    return { label: 'Active', cls: 'badge bg-success-subtle text-success' };
  }

  /** Raw getMembers rows may carry legacy spellings (CLOSE / CLOCE); list-panel rows are canonical 'Closed'. */
  get isSelectedMemberClosed(): boolean {
    const st = String(this.selectedMember?.status || '').trim().toUpperCase();
    return st === 'CLOSED' || st === 'CLOSE' || st === 'CLOCE';
  }

  get memberLocation(): string {
    const t = this.selectedMember?._memberTypeRaw ?? this.selectedMember?.memberType;
    return t === 'H' ? 'Door Delivery' : 'At Center';
  }

  close(): void {
    this.closed.emit();
  }
}
